/**
 * SupplierSignatureCard ✦
 *
 * A tiny, single-purpose card on the Supplier Studio dashboard.
 * The supplier uploads a small mark (max 220KB, PNG/JPG/WEBP/SVG);
 * EventDrop embeds it on every booking confirmation PDF above the
 * gold footer rule. Replace or remove anytime.
 *
 * Scope discipline:
 *   • One-shot upload, no cropping UI, no positioning controls.
 *   • Inline data URL stored on the supplier doc — no object
 *     storage needed for a single ~20KB asset.
 *   • Inline preview at the same proportion the PDF will draw it.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CheckCircle, PencilSimple, Trash, Sparkle } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupplierSignatureCard = () => {
  const fileRef = useRef(null);
  const [signatureUrl, setSignatureUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [justSaved, setJustSaved] = useState(false);

  const load = () => {
    axios.get(`${API}/studio/booking-defaults`)
      .then((r) => setSignatureUrl(r.data?.signature_url || ''))
      .catch(() => setSignatureUrl(''));
  };
  useEffect(load, []);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    setError('');
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 220 * 1024) { setError('Signature must be under 220 KB'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/studio/signature`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSignatureUrl(r.data?.signature_url || '');
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2200);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't save the signature");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onRemove = async () => {
    if (!window.confirm('Remove your signature from future confirmations?')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/studio/signature`);
      setSignatureUrl('');
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't remove the signature");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="supplier-signature-card"
      className="mt-10 rounded-xl"
      style={{
        background: 'rgba(255,255,255,0.025)',
        border: '1px solid rgba(194,157,82,0.18)',
        padding: 'clamp(18px, 3vw, 24px)',
      }}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div style={{ flex: 1, minWidth: 200 }}>
          <p className="text-[10px] font-black uppercase tracking-[0.32em] text-amber-300/80" style={{ fontFamily: 'Georgia, serif' }}>
            ✦ Signature
          </p>
          <h2 className="font-black text-xl mt-1" style={{ fontFamily: 'Georgia, serif' }}>
            Your mark on every confirmation
          </h2>
          <p className="text-white/55 text-[13px] italic mt-1" style={{ fontFamily: 'Georgia, serif' }}>
            A small mark — your handwritten name, your business stamp, anything you’d sign with. We’ll quietly add it to every booking confirmation PDF, above the footer rule.
          </p>
        </div>

        <div className="flex flex-col items-end gap-2" style={{ minWidth: 200 }}>
          {signatureUrl ? (
            <div
              data-testid="supplier-signature-preview"
              className="rounded-md p-3 flex items-center justify-center"
              style={{
                background: '#F8F2E3',
                border: '1px solid rgba(194,157,82,0.45)',
                minWidth: 180, maxWidth: 240, minHeight: 60,
              }}
            >
              <img
                src={signatureUrl}
                alt="Your signature"
                style={{ maxWidth: '100%', maxHeight: 54, objectFit: 'contain' }}
              />
            </div>
          ) : (
            <div
              className="rounded-md p-3 flex items-center justify-center italic"
              style={{
                background: 'rgba(0,0,0,0.18)',
                border: '1px dashed rgba(244,233,210,0.18)',
                minWidth: 180, maxWidth: 240, minHeight: 60,
                color: 'rgba(244,233,210,0.32)',
                fontFamily: 'Georgia, serif', fontSize: 12,
              }}
            >
              <Sparkle size={12} weight="fill" style={{ color: 'rgba(194,157,82,0.5)', marginRight: 6 }} />
              No mark yet
            </div>
          )}
          <div className="flex gap-2 items-center">
            <button
              onClick={onPick}
              disabled={busy}
              data-testid="supplier-signature-upload"
              className="text-[11px] flex items-center gap-1.5 px-3 py-1.5 rounded-full"
              style={{
                background: 'transparent',
                border: '1px solid rgba(194,157,82,0.45)',
                color: '#F4E9D2', fontStyle: 'italic',
                fontFamily: 'Georgia, serif',
                cursor: busy ? 'not-allowed' : 'pointer',
                opacity: busy ? 0.6 : 1,
              }}
            >
              {justSaved ? <><CheckCircle size={12} weight="fill" /> Settled</> : <><PencilSimple size={12} /> {signatureUrl ? 'Replace' : 'Upload signature ✦'}</>}
            </button>
            {signatureUrl && (
              <button
                onClick={onRemove}
                disabled={busy}
                data-testid="supplier-signature-remove"
                className="text-[11px] flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(244,233,210,0.14)',
                  color: 'rgba(244,233,210,0.5)', fontStyle: 'italic',
                  fontFamily: 'Georgia, serif', cursor: busy ? 'not-allowed' : 'pointer',
                }}
              >
                <Trash size={12} /> Remove
              </button>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
            onChange={onFile}
            style={{ display: 'none' }}
            data-testid="supplier-signature-input"
          />
        </div>
      </div>
      {error && (
        <p
          data-testid="supplier-signature-error"
          className="text-[12.5px] italic mt-3"
          style={{ color: '#E08F8F', fontFamily: 'Georgia, serif' }}
        >{error}</p>
      )}
    </section>
  );
};

export default SupplierSignatureCard;
